import { ErrorMessage } from '@components/shared';
import { NotificationSnackbar } from '@components/shared/notificationSnackbar';
import { MuiPhone } from '@components/shared/PhoneInput/PhoneInput';
import { TaModal } from '@components/shared/TaModal/TaModal';
import { EditableCard } from '@features/dashboard/specialistDashboard/editableCard/editableCard';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import { Chip } from '@mui/material';
import { validatePhoneNumberByCountryCode } from '@utils/phoneValidation';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateUser } from 'services/users';
import * as Yup from 'yup';

interface ContactDetailsProps {
	phone: string;
	email: string;
	readonly?: boolean;
}

const initialValues = {
	phone: '',
};

export const FarmerContactDetails = ({ phone, email, readonly = false }: ContactDetailsProps) => {
	const { t } = useTranslation();
	const mutate = useUpdateUser();
	const [open, setOpen] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [phoneValidationError, setPhoneValidationError] = useState<string>('');

	const contactDetailsValidationSchema = Yup.object({
		phone: Yup.string()
			.required('fieldNameError')
			.test('phone-validation', t('phoneNumberMatchError'), function (value) {
				if (!value) return false;

				const validation = validatePhoneNumberByCountryCode(value, t);
				if (!validation.isValid) {
					return this.createError({ message: validation.errorMessage });
				}
				return true;
			}),
	});

	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: contactDetailsValidationSchema,
		onSubmit: async (values) => {
			try {
				setIsLoading(true);

				// Final validation before submission
				const phoneValidation = validatePhoneNumberByCountryCode(values.phone, t);
				if (!phoneValidation.isValid) {
					setPhoneValidationError(phoneValidation.errorMessage || t('phoneNumberMatchError'));
					setIsLoading(false);
					return;
				}

				await mutate({
					phone: values.phone,
				});
				setOpen(false);
				setSnackbarOpen(true);
			} catch (error) {
				console.error('Update failed:', error);
			} finally {
				setIsLoading(false);
			}
		},
	});

	const { setValues } = formik;

	// Handle phone number change with real-time validation
	const handlePhoneChange = (phone: string) => {
		formik.setFieldValue('phone', phone);
		setPhoneValidationError('');

		// Real-time validation based on phone number's country code
		if (phone) {
			const validation = validatePhoneNumberByCountryCode(phone, t);
			if (!validation.isValid) {
				setPhoneValidationError(validation.errorMessage || t('phoneNumberMatchError'));
			}
		}
	};

	useEffect(() => {
		setValues({
			phone: phone,
		});
	}, [phone, setValues]);

	return (
		<EditableCard
			onEdit={() => setOpen(true)}
			className="responsive-farmer-card"
			title={t('contactDetails')}
			readonly={readonly}
		>
			<div className="flex flex-col gap-[20px] pb-5">
				<div className="flex gap-[10px]">
					<EmailOutlinedIcon className="text-secondary" />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">{email}</p>
					<Chip
						label={t('private')}
						variant="outlined"
						sx={{
							borderRadius: '5px',
							border: '1px solid #CED4DA',
							backgroundColor: '#F8F9FA',
							padding: '1px 5px',
							color: '#7B809A',
							fontFamily: 'Roboto',
							fontSize: '11px',
							fontStyle: 'normal',
							fontWeight: 400,
							lineHeight: '140%',
							height: '15px',
							textTransform: 'camelcase',
							'& .MuiChip-label': {
								padding: 0,
							},
						}}
					/>
				</div>
				<div className="flex gap-[10px]">
					<LocalPhoneOutlinedIcon className="text-secondary" />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">{phone}</p>
					<Chip
						label={t('private')}
						variant="outlined"
						sx={{
							borderRadius: '5px',
							border: '1px solid #CED4DA',
							backgroundColor: '#F8F9FA',
							padding: '1px 5px',
							color: '#7B809A',
							fontFamily: 'Roboto',
							fontSize: '11px',
							fontStyle: 'normal',
							fontWeight: 400,
							lineHeight: '140%',
							height: '15px',
							textTransform: 'camelcase',
							'& .MuiChip-label': {
								padding: 0,
							},
						}}
					/>
				</div>
			</div>
			<TaModal
				save={formik.handleSubmit}
				open={open}
				saveButtonLoading={isLoading}
				disableSaveButton={isLoading}
				onClose={() => setOpen(false)}
				title={t('contactDetails')}
				showActions
			>
				<div className="flex flex-col gap-[10px] mt-[20px]">
					<span className="roboto-regular text-[16px] leading-[140%]">{t('onlyTeam')}</span>
					<MuiPhone
						className="w-full"
						name="phone"
						placeholder={t('phoneNumber')}
						value={formik.values.phone}
						onChange={handlePhoneChange}
						onBlur={formik.handleBlur}
						error={
							(formik.touched.phone && Boolean(formik.errors.phone)) ||
							Boolean(phoneValidationError)
						}
					/>
					{((formik.touched.phone && formik.errors.phone) || phoneValidationError) && (
						<ErrorMessage
							message={
								phoneValidationError ||
								t(formik.errors.phone || '', {
									fieldName: t('phoneNumber'),
								})
							}
						/>
					)}
				</div>
			</TaModal>
			<NotificationSnackbar
				open={snackbarOpen}
				onClose={() => setSnackbarOpen(false)}
				title={t('successfullyUpdated')}
				success
				description={t('profileUpdated', {
					title: t('contactDetails'),
				})}
			/>
		</EditableCard>
	);
};
